import React, { useState, useEffect } from 'react';
import { Users, Plus, Mail, Shield, Edit2, Trash2, MoreVertical, Check, RefreshCw, Loader2, Building2, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import api, { teamApi, profileApi, chatApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { isEmailInCompanyDomain, resolveCompanyDomain } from '../utils/companyDomain';

const TeamManagement = () => {
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, by_role: {} });
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [startingChatMemberId, setStartingChatMemberId] = useState(null);

  const { member, user } = useAuth();
  const { canInviteTeamMember, getRemainingTeamSlots, refreshTierConfig } = useTier();
  const currentMemberId = member?.member_id;

  const handleStartChat = async (teamMember) => {
    const targetMemberId = teamMember?.member_id;
    if (!currentMemberId || !targetMemberId || targetMemberId === currentMemberId) return;

    try {
      setStartingChatMemberId(targetMemberId);
      const response = await chatApi.createConversation({
        conversation_type: 'Direct',
        participants: [currentMemberId, targetMemberId],
        title: teamMember.full_name || teamMember.email || 'Team member',
      });
      const conversationId = response.data?.conversation_id;
      if (!conversationId) throw new Error('Conversation ID was not returned');
      navigate(`/chat?conversation_id=${encodeURIComponent(conversationId)}`);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Unable to start chat'));
    } finally {
      setStartingChatMemberId(null);
    }
  };
  const companyDomain = resolveCompanyDomain({
    companyWebsite: profile?.company_website || member?.company_website,
    companyEmail: profile?.email || member?.email,
    userEmail: user?.email
  });

  const [inviteForm, setInviteForm] = useState({
    email: '',
    full_name: '',
    role: 'Company Member',
    phone: '',
    designation: '',
    branch: '',
    city: ''
  });

  // Company branches from Company Branches page (same localStorage as Colleagues)
  const [companyBranches, setCompanyBranches] = useState([]);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [branchContacts, setBranchContacts] = useState([]);
  const [branchInviteEmail, setBranchInviteEmail] = useState('');
  const canInviteMoreMembers = canInviteTeamMember();
  const remainingTeamSlots = getRemainingTeamSlots();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const profileRes = await profileApi.getProfile(currentMemberId);
      setProfile(profileRes.data);

      const companyId = profileRes.data.company_id;

      const [rolesRes, membersRes, statsRes] = await Promise.all([
        teamApi.getRoles(),
        teamApi.getTeamMembers(companyId),
        teamApi.getTeamStats(companyId)
      ]);

      setTeamMembers(membersRes.data);
      setStats(statsRes.data);
      await refreshTierConfig?.();

      // Load company branches from localStorage (same as Company Branches / Colleagues page)
      if (companyId && typeof window !== 'undefined') {
        try {
          const stored = window.localStorage.getItem(`company_branches_${companyId}`);
          const parsed = stored ? JSON.parse(stored) : [];
          const localBranches = Array.isArray(parsed) ? parsed.filter((b) => b && (b.branch_name || b.branch_type)) : [];
          // Merge with registration branches (including home branch)
          const merged = await loadBranchContacts(companyId, localBranches, membersRes.data);
          // Deduplicate branches by branch_name for the dropdown
          const seen = new Set();
          const allBranches = [];
          (merged || []).forEach((b) => {
            const name = (b.branch_name || '').trim();
            if (name && !seen.has(name)) {
              seen.add(name);
              allBranches.push(b);
            }
          });
          setCompanyBranches(allBranches);
        } catch {
          setCompanyBranches([]);
          await loadBranchContacts(companyId, [], membersRes.data);
        }
      } else {
        setBranchContacts([]);
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();

    if (!isEmailInCompanyDomain(inviteForm.email, companyDomain)) {
      toast.error(`Team invite email must use @${companyDomain}`);
      return;
    }

    setSendingInvite(true);
    try {
      await teamApi.inviteTeamMember(inviteForm, currentMemberId);
      toast.success('Invitation sent successfully!');
      setShowInviteDialog(false);
      setInviteForm({ email: '', full_name: '', role: 'Company Member', phone: '', designation: '', branch: '', city: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send invitation'));
    } finally {
      setSendingInvite(false);
    }
  };

  const showUpgradeTeamLimitMessage = () => {
    toast.error('Team member limit reached. Please upgrade to add more members.');
  };

  const handleOpenInviteDialog = () => {
    if (!canInviteMoreMembers) {
      showUpgradeTeamLimitMessage();
      return;
    }
    setShowInviteDialog(true);
  };

  const normalizeEmail = (value) => (value || '').trim().toLowerCase();

  const buildBranchContacts = (branches = [], existingMembers = []) => {
    const existingEmails = new Set(
      existingMembers
        .map((memberItem) => normalizeEmail(memberItem.email))
        .filter(Boolean)
    );
    const seenEmails = new Set();

    return branches
      .map((branch) => {
        const email = normalizeEmail(branch.contact_email);
        if (!email || existingEmails.has(email) || seenEmails.has(email)) return null;
        seenEmails.add(email);

        return {
          email,
          full_name: (branch.contact_name || '').trim() || 'Branch Contact',
          designation: (branch.contact_designation || '').trim(),
          phone: `${branch.contact_country_code || ''}${branch.contact_mobile || ''}`.trim(),
          branch: (branch.branch_name || '').trim(),
          city: [branch.city, branch.country].filter(Boolean).join(', '),
          status: branch.status || 'active',
        };
      })
      .filter((contact) => contact && contact.status === 'active');
  };

  const loadBranchContacts = async (companyId, localBranches = [], existingMembers = []) => {
    const mergedBranches = [...localBranches];

    try {
      const registrationRes = await api.get('/api/company-registration/my-registration');
      const registrationData = registrationRes.data || {};
      const registrationBranches = Array.isArray(registrationData.branches)
        ? registrationData.branches
        : [];

      // Add home branch from company info
      const companyInfo = registrationData.company_info || {};
      if (companyInfo.city || companyInfo.country) {
        mergedBranches.push({
          id: 'home-branch',
          branch_name: 'Head Office',
          branch_type: 'Head Office',
          country: companyInfo.country || '',
          city: companyInfo.city || '',
          contact_name: companyInfo.primary_contact_name || '',
          contact_designation: '',
          contact_email: companyInfo.primary_contact_email || '',
          contact_country_code: '',
          contact_mobile: companyInfo.primary_contact_phone || '',
          status: 'active',
        });
      }

      registrationBranches.forEach((branch, index) => {
        if (!branch || !(branch.branch_name || branch.contact_email || branch.city)) return;
        mergedBranches.push({
          id: branch.id || `registration-branch-${index}`,
          branch_name: branch.branch_name || branch.city || 'Branch',
          branch_type: branch.branch_type || 'Branch Office',
          country: branch.country || '',
          city: branch.city || '',
          contact_name: branch.contact_name || '',
          contact_designation: branch.contact_designation || '',
          contact_email: branch.contact_email || '',
          contact_country_code: branch.contact_country_code || '',
          contact_mobile: branch.contact_mobile || '',
          status: 'active',
        });
      });
    } catch (error) {
      console.debug('Registration branches unavailable for team page:', error);
    }

    if (!companyId) {
      setBranchContacts([]);
      return mergedBranches;
    }

    setBranchContacts(buildBranchContacts(mergedBranches, existingMembers));
    return mergedBranches;
  };

  const handleInviteBranchContact = async (contact) => {
    if (!currentMemberId) return;
    if (!canInviteMoreMembers) {
      showUpgradeTeamLimitMessage();
      return;
    }

    setBranchInviteEmail(contact.email);
    try {
      await teamApi.inviteTeamMember(
        {
          email: contact.email,
          full_name: contact.full_name,
          role: 'Company Member',
          phone: contact.phone || '',
          designation: contact.designation || '',
          branch: contact.branch || '',
          city: contact.city || ''
        },
        currentMemberId
      );
      toast.success('Invitation sent successfully!');
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send invitation'));
    } finally {
      setBranchInviteEmail('');
    }
  };

  const handleUpdateMember = async (teamMemberId, updates) => {
    try {
      await teamApi.updateTeamMember(teamMemberId, updates, currentMemberId);
      toast.success('Team member updated');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update'));
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    try {
      await teamApi.removeTeamMember(memberToRemove.team_member_id, currentMemberId);
      toast.success('Team member removed');
      setShowRemoveDialog(false);
      setMemberToRemove(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to remove'));
    }
  };

  const handleResendInvite = async (teamMemberId) => {
    try {
      await teamApi.resendInvite(teamMemberId, currentMemberId);
      toast.success('Invitation resent');
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to resend'));
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      'Company Admin': 'bg-indigo-100 text-indigo-700',
      'Company Member': 'bg-slate-100 text-slate-700'
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  const knownBranchOptions = Array.from(
    new Map(
      [
        ...companyBranches.map((branch) => ({
          name: (branch.branch_name || '').trim(),
          city: [branch.city, branch.country].filter(Boolean).join(', ')
        })),
        ...teamMembers.map((memberItem) => ({
          name: (memberItem.branch || '').trim(),
          city: (memberItem.city || '').trim()
        })),
        {
          name: (selectedMember?.branch || '').trim(),
          city: (selectedMember?.city || '').trim()
        }
      ]
        .filter((branch) => branch.name)
        .map((branch) => [branch.name, branch])
    ).values()
  ).sort((a, b) => a.name.localeCompare(b.name));

  // Display list: API now returns company admin(s) first, then invited members — no need to prepend
  const branchesList = Array.from(new Set(teamMembers.map((m) => m.branch).filter(Boolean))).sort();
  const [branchFilter, setBranchFilter] = useState('');
  const displayMembers = branchFilter
    ? teamMembers.filter((m) => (m.branch || '') === branchFilter)
    : teamMembers;
  const isAdminRow = (m) => m && (m.role === 'Company Admin' || m.isCompanyAdmin);
  const isCompanyAdmin = profile?.has_admin_rights === true;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'Pending':
        return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
      case 'Inactive':
        return <Badge className="bg-gray-100 text-gray-700">Inactive</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading team...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
          <p className="text-gray-600 mt-1">Manage your team members and permissions</p>
        </div>
        <div className="flex w-full flex-col items-stretch gap-1 md:w-auto md:items-end">
          <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
            <DialogTrigger asChild>
              <Button
                type="button"
                className="bg-blue-600 hover:bg-blue-700 text-white data-[blocked=true]:cursor-not-allowed data-[blocked=true]:opacity-60"
                data-testid="invite-team-btn"
                data-blocked={!canInviteMoreMembers}
                aria-disabled={!canInviteMoreMembers}
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenInviteDialog();
                }}
              >
                <Plus size={18} className="mr-2" />
                Invite Team Member
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
            <DialogHeader className="px-6 pt-10 pb-2 shrink-0">
              <DialogTitle>Invite Team Member</DialogTitle>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 min-h-0 px-6 pb-6">
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    required
                    placeholder={companyDomain ? `colleague@${companyDomain}` : 'colleague@company.com'}
                  />
                  {companyDomain && (
                    <p className="text-xs text-gray-500 mt-1">
                      Allowed domain: @{companyDomain}
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={inviteForm.full_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                    required
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label htmlFor="branch">Branch *</Label>
                  <select
                    id="branch"
                    className="w-full px-3 py-2 border rounded-md bg-white"
                    value={inviteForm.branch}
                    onChange={(e) => {
                      const branchName = e.target.value;
                      const branch = companyBranches.find((b) => (b.branch_name || '') === branchName);
                      const cityDisplay = branch
                        ? [branch.city, branch.country].filter(Boolean).join(', ')
                        : '';
                      setInviteForm((prev) => ({ ...prev, branch: branchName, city: cityDisplay }));
                    }}
                    required
                  >
                    <option value="">Select branch...</option>
                    {companyBranches.map((b) => (
                      <option key={b.id || b.branch_name} value={b.branch_name || ''}>
                        {b.branch_name || 'Unnamed branch'}
                      </option>
                    ))}
                  </select>
                  {companyBranches.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">Add branches from Company Branches page first.</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="city">City (from branch)</Label>
                  <Input
                    id="city"
                    value={inviteForm.city}
                    readOnly
                    className="bg-gray-50 text-gray-700 cursor-not-allowed"
                    placeholder="Select a branch"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <div className="w-full px-3 py-2 border rounded-md bg-gray-50 text-gray-700 border-gray-200 text-sm font-normal">
                    Company Member
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Invited users join as Company Member. You can control their permissions after they join.</p>
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={inviteForm.phone}
                    onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="designation">Job Title</Label>
                  <Input
                    id="designation"
                    value={inviteForm.designation}
                    onChange={(e) => setInviteForm({ ...inviteForm, designation: e.target.value })}
                    placeholder="e.g., Senior Operations Manager"
                  />
                </div>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-900">Company Member</p>
                <p className="text-xs text-blue-700 mt-1">Permissions can be controlled by you (Company Admin) after they join, via Manage Permissions.</p>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowInviteDialog(false)} disabled={sendingInvite}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={sendingInvite}>
                  {sendingInvite ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <Mail size={16} className="mr-2" />
                  )}
                  {sendingInvite ? 'Sending...' : 'Send Invitation'}
                </Button>
              </div>
            </form>
            </div>
          </DialogContent>
        </Dialog>
        {!canInviteMoreMembers && (
          <p className="text-sm text-amber-700 bg-amber-50 px-3 py-1.5 rounded max-w-sm text-right">
            Team member limit reached{remainingTeamSlots === 0 ? '' : ` (${remainingTeamSlots} slots left)`}. <Link to="/upgrade" className="font-medium underline">Upgrade your plan</Link> to invite more.
          </p>
        )}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Members</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">{stats.total}</p>
              </div>
              <Users size={40} className="text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Active</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{stats.active}</p>
              </div>
              <Check size={40} className="text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending Invites</p>
                <p className="text-3xl font-bold text-amber-600 mt-1">{stats.pending}</p>
              </div>
              <Mail size={40} className="text-amber-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Roles Used</p>
                <p className="text-3xl font-bold text-purple-600 mt-1">{Object.keys(stats.by_role).length}</p>
              </div>
              <Shield size={40} className="text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Members List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Team Members</CardTitle>
            {branchesList.length > 0 && (
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                <Label htmlFor="branch-filter" className="text-sm text-gray-600 whitespace-nowrap">Branch</Label>
                <select
                  id="branch-filter"
                  className="w-full min-w-0 px-3 py-1.5 border rounded-md text-sm sm:w-auto"
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                >
                  <option value="">All branches</option>
                  {branchesList.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 hidden sm:inline">Filter by branch to assign business to the right team.</p>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {branchContacts.length > 0 && (
            <div className="mb-6">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mb-3">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900">Branch Contacts Pending Invite</p>
                  <p className="text-sm text-gray-500">Contacts added during company or branch setup but not invited yet.</p>
                </div>
                <Badge className="bg-orange-100 text-orange-700">{branchContacts.length} pending</Badge>
              </div>
              <div className="space-y-3">
                {branchContacts.map((contact) => (
                  <div
                    key={contact.email}
                    className="flex flex-col gap-4 rounded-lg border border-orange-200 bg-orange-50/60 p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div className="flex min-w-0 items-start space-x-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700">
                        <Building2 size={20} />
                      </div>
                      <div className="min-w-0">
                        <p className="break-words font-medium text-gray-900">{contact.full_name}</p>
                        <p className="break-all text-sm text-gray-500">{contact.email}</p>
                        <p className="mt-0.5 break-words text-xs text-gray-600">
                          {[contact.branch, contact.city].filter(Boolean).join(' • ') || 'Branch contact'}
                        </p>
                        {contact.designation && (
                          <p className="mt-1 break-words text-xs text-gray-500">{contact.designation}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      className="w-full bg-blue-600 text-white hover:bg-blue-700 data-[blocked=true]:cursor-not-allowed data-[blocked=true]:opacity-60 disabled:opacity-60 md:w-auto"
                      data-blocked={!canInviteMoreMembers}
                      aria-disabled={!canInviteMoreMembers}
                      disabled={branchInviteEmail === contact.email}
                      onClick={() => handleInviteBranchContact(contact)}
                    >
                      {branchInviteEmail === contact.email ? (
                        <Loader2 size={16} className="mr-2 animate-spin" />
                      ) : (
                        <Mail size={16} className="mr-2" />
                      )}
                      {branchInviteEmail === contact.email ? 'Sending...' : 'Invite as Team Member'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {displayMembers.length === 0 ? (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No team members yet</p>
              <p className="text-sm text-gray-400 mt-1">Invite colleagues to collaborate on the platform</p>
            </div>
          ) : (
            <div className="space-y-3">
              {displayMembers.map((member) => (
                <div
                  key={member.team_member_id || member.member_id}
                  className="flex flex-col gap-4 rounded-lg border p-4 transition-colors hover:border-blue-300 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-start space-x-4">
                    <img
                      src={`https://api.dicebear.com/7.x/initials/svg?seed=${member.full_name}`}
                      alt={member.full_name}
                      className="h-12 w-12 shrink-0 rounded-full"
                    />
                    <div className="min-w-0">
                      <p className="break-words font-medium text-gray-900">{member.full_name}</p>
                      <p className="break-all text-sm text-gray-500">{member.email}</p>
                      {(member.branch || member.city) && (
                        <p className="mt-0.5 break-words text-xs text-gray-600">
                          {[member.branch, member.city].filter(Boolean).join(' • ')}
                        </p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <Badge className={getRoleColor(member.role)}>{member.role}</Badge>
                        {!isAdminRow(member) && getStatusBadge(member.status)}
                      </div>
                    </div>
                  </div>

                  <div className="flex w-full shrink-0 items-center justify-end space-x-2 sm:w-auto">
                    {member.member_id && member.member_id !== currentMemberId && member.status !== 'Pending' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={startingChatMemberId === member.member_id}
                        onClick={() => handleStartChat(member)}
                      >
                        {startingChatMemberId === member.member_id ? (
                          <Loader2 size={15} className="mr-2 animate-spin" />
                        ) : (
                          <MessageSquare size={15} className="mr-2" />
                        )}
                        {startingChatMemberId === member.member_id ? 'Opening…' : 'Start Chat'}
                      </Button>
                    )}
                    {!isAdminRow(member) && (
                      <>
                        <div className="hidden md:flex items-center space-x-1">
                          {member.can_manage_team && (
                            <Badge variant="outline" className="text-xs">
                              <Users size={10} className="mr-1" />
                              Team
                            </Badge>
                          )}
                          {member.can_approve_deals && (
                            <Badge variant="outline" className="text-xs">
                              <Check size={10} className="mr-1" />
                              Deals
                            </Badge>
                          )}
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setSelectedMember(member);
                              setShowEditDialog(true);
                            }}>
                              <Edit2 size={14} className="mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedMember(member);
                              setShowPermissionsDialog(true);
                            }}>
                              <Shield size={14} className="mr-2" />
                              Manage Permissions
                            </DropdownMenuItem>
                            {member.status === 'Pending' && (
                              <DropdownMenuItem onClick={() => handleResendInvite(member.team_member_id)}>
                                <RefreshCw size={14} className="mr-2" />
                                Resend Invite
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => { setMemberToRemove(member); setShowRemoveDialog(true); }}
                            >
                              <Trash2 size={14} className="mr-2" />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    )}
                    {isAdminRow(member) && (
                      <Badge variant="outline" className="text-xs text-gray-500">Full access</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Role Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(stats.by_role).map(([role, count]) => (
              <div key={role} className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <Badge className={`mt-1 ${getRoleColor(role)}`}>{role}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit Member Dialog - Company Member: role is fixed; Job Title, Branch (dropdown), City (from branch, read-only) */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-10 pb-2 shrink-0">
            <DialogTitle>Edit Team Member</DialogTitle>
          </DialogHeader>
          {selectedMember && !isAdminRow(selectedMember) && (
            <div className="overflow-y-auto flex-1 min-h-0 px-6 pb-6 space-y-4">
              <div>
                <Label>Role</Label>
                <p className="text-sm text-gray-600 mt-1">Company Member (permissions controlled by Company Admin)</p>
              </div>
              <div>
                <Label>Job Title</Label>
                <p className="text-xs text-gray-500 mt-1">This is the member&apos;s designation, not their access role. Team roles remain only Company Admin or Company Member.</p>
                <Input
                  value={selectedMember.designation || ''}
                  onChange={(e) => setSelectedMember({ ...selectedMember, designation: e.target.value })}
                  placeholder="e.g., Senior Operations Manager"
                />
              </div>
              <div>
                <Label>Branch</Label>
                <select
                  className="w-full px-3 py-2 border rounded-md bg-white"
                  value={selectedMember.branch || ''}
                  onChange={(e) => {
                    const branchName = e.target.value;
                    const branch = knownBranchOptions.find((b) => b.name === branchName);
                    const cityDisplay = branch?.city || '';
                    setSelectedMember((prev) => ({
                      ...prev,
                      branch: branchName,
                      city: cityDisplay || prev.city || ''
                    }));
                  }}
                >
                  <option value="">Select branch...</option>
                  {knownBranchOptions.map((branch) => (
                    <option key={branch.name} value={branch.name}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                {knownBranchOptions.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No company branches found yet. Add branches from Company Branches, or save a branch on another team member first.</p>
                )}
              </div>
              <div>
                <Label>City (from branch)</Label>
                <Input
                  value={selectedMember.city || ''}
                  readOnly
                  className="bg-gray-50 text-gray-700 cursor-not-allowed"
                  placeholder="Select a branch"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
                <Button
                  onClick={() => handleUpdateMember(selectedMember.team_member_id, {
                    designation: selectedMember.designation,
                    branch: selectedMember.branch || '',
                    city: selectedMember.city || ''
                  })}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-10 pb-2 shrink-0">
            <DialogTitle>Manage Permissions</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="overflow-y-auto flex-1 min-h-0 px-6 pb-6 space-y-4">
              <p className="text-sm text-gray-600">
                Configure permissions for <strong>{selectedMember.full_name}</strong>
              </p>
              <div className="space-y-3">
                {[
                  { key: 'can_manage_team', label: 'Manage Team', desc: 'Invite and manage team members' },
                  { key: 'can_approve_deals', label: 'Approve Deals', desc: 'Nominate and approve business deals' },
                  { key: 'can_nominate_business', label: 'Nominate Business', desc: 'Nominate members for quotes' },
                  { key: 'can_view_analytics', label: 'View Analytics', desc: 'Access analytics dashboard' },
                  { key: 'can_manage_quotes', label: 'Manage Quotes', desc: 'Create and manage quotes' }
                ].map((perm) => (
                  <label key={perm.key} className="flex items-start p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedMember[perm.key] || false}
                      onChange={(e) => setSelectedMember({ ...selectedMember, [perm.key]: e.target.checked })}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <p className="font-medium">{perm.label}</p>
                      <p className="text-sm text-gray-500">{perm.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowPermissionsDialog(false)}>Cancel</Button>
                <Button
                  onClick={async () => {
                    await teamApi.updatePermissions(
                      selectedMember.team_member_id,
                      {
                        can_manage_team: selectedMember.can_manage_team,
                        can_approve_deals: selectedMember.can_approve_deals,
                        can_nominate_business: selectedMember.can_nominate_business,
                        can_view_analytics: selectedMember.can_view_analytics,
                        can_manage_quotes: selectedMember.can_manage_quotes
                      },
                      currentMemberId
                    );
                    toast.success('Permissions updated');
                    setShowPermissionsDialog(false);
                    fetchData();
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Save Permissions
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog open={showRemoveDialog} onOpenChange={(open) => { setShowRemoveDialog(open); if (!open) setMemberToRemove(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to remove <strong>{memberToRemove?.full_name}</strong> from the team? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => { setShowRemoveDialog(false); setMemberToRemove(null); }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemoveMember}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default TeamManagement;
